<template>
  <div class="login">
    <GoblinIcon class="hero" variant="mark" :size="64" label="loggob" />
    <form class="card" @submit.prevent="submit">
      <h1 class="brand">loggob</h1>
      <p class="tagline">docker logs, without the ceremony</p>

      <label class="field">
        <span>Username</span>
        <LgInput
          v-model="username"
          type="text"
          autocomplete="username"
          aria-label="Username"
          autofocus
          required
        />
      </label>
      <label class="field">
        <span>Password</span>
        <LgInput
          v-model="password"
          type="password"
          autocomplete="current-password"
          aria-label="Password"
          required
        />
      </label>

      <p v-if="error" class="error" role="alert">{{ error }}</p>

      <LgButton variant="primary" type="submit" block :disabled="loading">
        {{ loading ? 'Signing in…' : 'Sign in' }}
      </LgButton>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { api, ApiError } from '../api.js';
import LgInput from '../ui/LgInput.vue';
import LgButton from '../ui/LgButton.vue';
import GoblinIcon from '../components/GoblinIcon.vue';

const emit = defineEmits<{ 'login-success': [] }>();

const username = ref('');
const password = ref('');
const loading = ref(false);
const error = ref('');

async function submit() {
  loading.value = true;
  error.value = '';
  try {
    await api.login(username.value, password.value);
    emit('login-success');
  } catch (e) {
    if (e instanceof ApiError && e.status === 429) {
      error.value = 'Too many attempts. Try again in a minute.';
    } else if (e instanceof ApiError && e.status === 401) {
      error.value = 'Invalid username or password.';
    } else {
      error.value = e instanceof Error ? e.message : 'Login failed.';
    }
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
.login {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--lg-space-5);
  padding: var(--lg-space-5);
}
.hero {
  color: var(--lg-text);
}
.card {
  width: 100%;
  max-width: 340px;
  background: var(--lg-bg-raised);
  border: 1px solid var(--lg-border);
  border-radius: var(--lg-radius-lg);
  padding: var(--lg-space-7) var(--lg-space-6);
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-3);
}
.brand {
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-2xl);
  margin: 0;
  letter-spacing: -0.02em;
}
.tagline {
  margin: 0 0 var(--lg-space-1);
  color: var(--lg-text-dim);
  font-size: var(--lg-fs-md);
}
.field {
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-1);
  font-size: var(--lg-fs-md);
  color: var(--lg-text-dim);
}
.error {
  margin: 0;
  color: var(--lg-level-error);
  font-size: var(--lg-fs-md);
}
</style>
